import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import {
    Card,
    Input,
    Button,
    Form,
    InputNumber,
    message,
    Divider,
    Typography,
    Row,
    Col,
    Table,
    Modal,
    Space,
    Popconfirm,
    Tag,
} from "antd";
import {
    SaveOutlined,
    EnvironmentOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { Search } = Input;

export default function AddWorklocation() {
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const { authData, backUrl } = useContext(AuthContext);

    // ------- List state -------
    const [locations, setLocations] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [searchText, setSearchText] = useState("");

    // ------- Edit modal state -------
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editForm] = Form.useForm();

    const authHeaders = {
        Authorization: `Bearer ${authData?.access}`,
        "Content-Type": "application/json",
    };

    // ✅ Fetch all work locations
    const fetchLocations = async () => {
        setLoadingList(true);
        try {
            const res = await axios.get(`${backUrl}/api/emp/worklocation/`, {
                headers: authHeaders,
            });
            const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
            setLocations(data);
        } catch (err) {
            message.error("Failed to load work locations.");
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        fetchLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ Submit new work location
    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const payload = {
                name: values.name,
                full_name: values.full_name || null,
                latitude: values.latitude ?? null,
                longitude: values.longitude ?? null,
                radius: values.radius ?? null,
                bss_project: values.bss_project ?? null,
                short_latitude: values.short_latitude ?? null,
                short_longitude: values.short_longitude ?? null,
                short_radius: values.short_radius ?? null,
            };

            await axios.post(`${backUrl}/api/emp/worklocation/`, payload, {
                headers: authHeaders,
            });

            message.success("Work location added successfully!");
            form.resetFields();
            fetchLocations();
        } catch (err) {
            let errMsg = "Failed to add work location.";

            if (err.response) {
                errMsg =
                    err.response.data?.message ||
                    err.response.data?.detail ||
                    err.response.data?.name?.[0] ||
                    JSON.stringify(err.response.data);
            } else if (err.request) {
                errMsg = "No response from server. Please try again.";
            } else {
                errMsg = err.message;
            }

            message.error(errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    // ✅ Open edit modal, prefill values
    const handleEditClick = (record) => {
        setEditingRecord(record);
        editForm.setFieldsValue({
            name: record.name,
            full_name: record.full_name,
            latitude: record.latitude,
            longitude: record.longitude,
            radius: record.radius,
            short_latitude: record.short_latitude,
            short_longitude: record.short_longitude,
            short_radius: record.short_radius,
        });
        setEditModalOpen(true);
    };

    // ✅ Submit edit (PATCH)
    const handleEditSubmit = async (values) => {
        setSaving(true);
        try {
            const payload = {
                name: values.name,
                full_name: values.full_name || null,
                latitude: values.latitude ?? null,
                longitude: values.longitude ?? null,
                radius: values.radius ?? null,
                short_latitude: values.short_latitude ?? null,
                short_longitude: values.short_longitude ?? null,
                short_radius: values.short_radius ?? null,
            };

            await axios.patch(
                `${backUrl}/api/emp/worklocation/${editingRecord.id}/`,
                payload,
                { headers: authHeaders }
            );

            message.success("Work location updated successfully!");
            setEditModalOpen(false);
            setEditingRecord(null);
            fetchLocations();
        } catch (err) {
            let errMsg = "Failed to update work location.";
            if (err.response) {
                errMsg =
                    err.response.data?.message ||
                    err.response.data?.detail ||
                    err.response.data?.name?.[0] ||
                    JSON.stringify(err.response.data);
            } else if (err.request) {
                errMsg = "No response from server. Please try again.";
            } else {
                errMsg = err.message;
            }
            message.error(errMsg);
        } finally {
            setSaving(false);
        }
    };

    // ✅ Delete a work location
    const handleDelete = async (record) => {
        try {
            await axios.delete(`${backUrl}/api/emp/worklocation/${record.id}/`, {
                headers: authHeaders,
            });
            message.success("Work location deleted.");
            fetchLocations();
        } catch (err) {
            message.error("Failed to delete work location.");
        }
    };

    const filteredLocations = locations.filter((loc) => {
        const q = searchText.toLowerCase();
        return (
            loc.name?.toLowerCase().includes(q) ||
            loc.full_name?.toLowerCase().includes(q)
        );
    });

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text) => <Tag color="#0e497b">{text}</Tag>,
        },
        {
            title: "Full Name",
            dataIndex: "full_name",
            key: "full_name",
            render: (text) => text || <span style={{ color: "#999" }}>—</span>,
        },
        {
            title: "Geofence (Full)",
            key: "geofence_full",
            render: (_, record) =>
                record.latitude && record.longitude ? (
                    <span>
                        {record.latitude}, {record.longitude}{" "}
                        {record.radius ? `(±${record.radius}m)` : ""}
                    </span>
                ) : (
                    <span style={{ color: "#999" }}>—</span>
                ),
        },
        {
            title: "Geofence (Short)",
            key: "geofence_short",
            render: (_, record) =>
                record.short_latitude && record.short_longitude ? (
                    <span>
                        {record.short_latitude}, {record.short_longitude}{" "}
                        {record.short_radius ? `(±${record.short_radius}m)` : ""}
                    </span>
                ) : (
                    <span style={{ color: "#999" }}>—</span>
                ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEditClick(record)}
                    />
                    <Popconfirm
                        title="Delete this work location?"
                        description="This action cannot be undone."
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record)}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "24px" }}>
            {/* ---------- Add Form ---------- */}
            <Card
                style={{
                    maxWidth: 700,
                    margin: "0 auto 24px",
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <Title level={4} style={{ textAlign: "center", color: "#0e497b" }}>
                    <EnvironmentOutlined style={{ marginRight: 8 }} />
                    Add Work Location
                </Title>

                <Divider />

                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[
                                    { required: true, message: "Please enter a short, unique name." },
                                ]}
                            >
                                <Input placeholder="e.g., HEAD OFFICE" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Full Name" name="full_name">
                                <Input placeholder="e.g., Head Office - Chennai" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left" plain>
                        Geofence (Full)
                    </Divider>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Latitude" name="latitude">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    placeholder="e.g., 13.08268400"
                                    step={0.00000001}
                                    stringMode
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Longitude" name="longitude">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    placeholder="e.g., 80.27070700"
                                    step={0.00000001}
                                    stringMode
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Radius (meters)" name="radius">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    placeholder="e.g., 200"
                                    min={0}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left" plain>
                        Geofence (Short)
                    </Divider>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Short Latitude" name="short_latitude">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    placeholder="e.g., 13.08268400"
                                    step={0.00000001}
                                    stringMode
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Short Longitude" name="short_longitude">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    placeholder="e.g., 80.27070700"
                                    step={0.00000001}
                                    stringMode
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Short Radius (meters)" name="short_radius">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    placeholder="e.g., 50"
                                    min={0}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item style={{ marginTop: 12 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={submitting}
                            icon={<SaveOutlined />}
                            block
                            style={{
                                backgroundColor: "#0e497b",
                                borderColor: "#0e497b",
                                height: 40,
                                borderRadius: 6,
                            }}
                        >
                            Submit Work Location
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            {/* ---------- List / Edit Table ---------- */}
            <Card
                style={{
                    maxWidth: 1100,
                    margin: "0 auto",
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Title level={4} style={{ margin: 0, color: "#0e497b" }}>
                            <EnvironmentOutlined style={{ marginRight: 8 }} />
                            All Work Locations
                        </Title>
                    </Col>
                    <Col>
                        <Space>
                            <Search
                                placeholder="Search by name"
                                allowClear
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 220 }}
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchLocations}
                                loading={loadingList}
                            />
                        </Space>
                    </Col>
                </Row>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={filteredLocations}
                    loading={loadingList}
                    pagination={{ pageSize: 8, showSizeChanger: false }}
                    scroll={{ x: 700 }}
                />
            </Card>

            {/* ---------- Edit Modal ---------- */}
            <Modal
                title={
                    <span>
                        <EditOutlined style={{ marginRight: 8, color: "#0e497b" }} />
                        Edit Work Location
                    </span>
                }
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false);
                    setEditingRecord(null);
                }}
                footer={null}
                destroyOnClose
                width={700}
            >
                <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[
                                    { required: true, message: "Please enter a short, unique name." },
                                ]}
                            >
                                <Input placeholder="e.g., HEAD OFFICE" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Full Name" name="full_name">
                                <Input placeholder="e.g., Head Office - Chennai" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Title level={5} style={{ marginTop: 8 }}>
                        Geofence (Full)
                    </Title>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Latitude" name="latitude">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    step={0.00000001}
                                    stringMode
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Longitude" name="longitude">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    step={0.00000001}
                                    stringMode
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Radius (meters)" name="radius">
                                <InputNumber style={{ width: "100%" }} min={0} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Title level={5} style={{ marginTop: 8 }}>
                        Geofence (Short)
                    </Title>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Short Latitude" name="short_latitude">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    step={0.00000001}
                                    stringMode
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Short Longitude" name="short_longitude">
                                <InputNumber
                                    style={{ width: "100%" }}
                                    step={0.00000001}
                                    stringMode
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Short Radius (meters)" name="short_radius">
                                <InputNumber style={{ width: "100%" }} min={0} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item style={{ marginTop: 12, marginBottom: 0 }}>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={saving}
                                style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                            >
                                Save Changes
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}